import { Col, Progress, Row } from 'antd';
// import { UploadFile } from 'antd/lib/upload/interface';
import { CancelButton, ConfirmButton } from 'components/common/DefaultFormButton';
import DefaultModal from 'components/common/DefaultModal';
import { InputComponent } from 'components/common/Input';
import { ICheckboxPropsInCheckboxGroup } from 'components/common/Input/new/types';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { InputProps } from 'components/InputFieldsGenerator/types';
import { FORMAT_DATE_TIME_US_FOR_DISPLAY } from 'constants/days';
import { usStatesSelectOptions } from 'constants/usStates';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AuthSelectors from 'redux/auth/auth.selectors';
// import { useDispatch } from 'react-redux';
// import FileAction from 'redux/file/file.actions';
import { AppointmentNotaryType, IAppointmentState, ShippingServiceType } from 'types/appointments';
import { GeneralFunction } from 'types/common';
import { DocumentSubType, IFile } from 'types/file';
import { IAppointmentTypeWithConfigurations } from 'types/services';
import { ITransaction } from 'types/transaction';
import { getFileViewUrl } from 'utils';
// import { appointmentTypeOptionsArray } from 'types/transaction';
import IntlMessages from 'utils/IntlMessages';
import { v4 as uuidV4 } from 'uuid';
import * as Yup from 'yup';

import { INotaryAppointmentInputProps, ISignatoryInputProps } from '../../types';

const AppointmentModal: React.FC<{
  filePercentageUpload?: number;
  modalType?: string;
  closeModal: GeneralFunction;
  addAppointment: (value: INotaryAppointmentInputProps, id: string) => void;
  editAppointment: (value: INotaryAppointmentInputProps, id: string) => void;
  data?: IAppointmentState;
  signatoriesData?: Record<string, ISignatoryInputProps>;
  isLoading?: boolean;
  transaction?: ITransaction;
  fileUploadLoading?: boolean;
  transactionTypeConfigurations?: IAppointmentTypeWithConfigurations;
}> = ({
  modalType,
  closeModal,
  addAppointment,
  editAppointment,
  data,
  signatoriesData,
  isLoading,
  transaction,
  filePercentageUpload,
  fileUploadLoading,
  transactionTypeConfigurations,
}) => {
  const documentsData = transaction?.documents?.find((doc) => doc.subType === DocumentSubType.COMMON);

  // const dispatch = useDispatch();
  const [labelProvidedByClient, setLabelProvidedByClient] = useState(false);

  useEffect(() => {
    if (data && data.autoGenShippingLabel !== undefined) setLabelProvidedByClient(!data?.autoGenShippingLabel);
  }, [data]);

  const checkboxProps: ICheckboxPropsInCheckboxGroup[] = useMemo(() => {
    const files: IFile[] = documentsData?.files || [];
    return files.map(
      (file): ICheckboxPropsInCheckboxGroup => ({
        label: `${file.name}${file.details?.description ? ` - ${file.details?.description}` : ''}`,
        name: file.id,
        value: (data?.files || []).some((af) => af.id === file.id),
      }),
    );
  }, [data, documentsData]);

  const appointmentSchema = useMemo(
    () => ({
      // type: Yup.string().required('Required'),
      signatoryIds: Yup.array().of(Yup.string()).min(1, 'Required').required('Required'),
      date: Yup.string().required('Required'),
      signedTime: Yup.string().required('Required'),
      // duration: Yup.string()
      //   .matches(/^[0-9]*$/, 'Wrong format')
      //   .required('Required'),
      address1: Yup.string().required('Required'),
      // address2: Yup.string().required('Required'),
      city: Yup.string().required('Required'),
      state: Yup.string().required('Required'),
      zip: Yup.string()
        .matches(/(^\d{5}$)|(^\d{9}$)|(^\d{5}-\d{4}$)/, 'Wrong format')
        .required('Required'),
      description: Yup.string().notRequired(),
    }),
    [],
  );

  const shippingLabelSchema = useMemo(
    () =>
      labelProvidedByClient
        ? {
            labelFiles: Yup.array().min(1, 'Required'),
          }
        : {
            labelFiles: Yup.array().notRequired(),
          },
    [labelProvidedByClient],
  );

  // const type = data?.type;
  const selectedSignatoriesIds = data?.signatoryIds || [];
  // const signatories = data?.signatoryIds || [];
  // const description = data?.description || '';
  const address = data?.address;
  const address1 = address?.address1 || '';
  const address2 = address?.address2 || '';
  const city = address?.city || '';
  const state = address?.state || '';
  const zip = address?.zip || '';
  const appointmentDuration = data?.duration || transactionTypeConfigurations?.IN_PERSON?.duration || 0;
  const duration = appointmentDuration / 3600;
  const date = data?.date ? moment(data?.date || '').format(FORMAT_DATE_TIME_US_FOR_DISPLAY) : '';
  const dateTime = date?.toString() || '';
  const [dateData, signedTimeData] = dateTime ? dateTime.split(' ') : ['', ''];
  const fileGroupProps: Record<string, boolean> = useMemo(() => {
    const temp: Record<string, boolean> = {};
    checkboxProps.forEach((p) => {
      temp[p.name] = p.value || false;
    });
    return temp;
  }, [checkboxProps]);
  const token = useSelector(AuthSelectors.getAccessToken);
  const notaryAppointmentInitValues = {
    // type,
    date: dateData,
    signedTime: signedTimeData,
    address1,
    address2,
    longtitude: address?.coordinate?.[0],
    latitude: address?.coordinate?.[1],
    city,
    state,
    zip,
    signatoryIds: selectedSignatoriesIds,
    // duration,
    fileGroup: fileGroupProps,
    // description,
    autoGenShippingLabel: !labelProvidedByClient,
    labelFiles:
      data?.labelFiles?.map((f) => ({
        uid: f.id,
        name: f.name,
        url: getFileViewUrl(f.id, token || '', f.name),
        status: 'done',
      })) || [],
    shippingServiceType: data?.shippingOptions?.serviceType || 'PRIORITY_OVERNIGHT',
  };

  const signatoriesSelectOptions = Object.entries(signatoriesData || {}).map(([sigId, sig]) => ({
    label: `${sig.firstName} ${sig.lastName}`,
    value: sigId,
  }));

  const formFields: InputProps[] = [
    {
      label: 'Signatories',
      name: 'signatoryIds',
      type: INPUT_TYPE.SELECT,
      defaultValue: selectedSignatoriesIds,
      inputOptions: signatoriesSelectOptions,
      inputSettings: { mode: 'multiple' },
    },
    // {
    //   label: 'Description',
    //   name: 'description',
    // },
    {
      label: 'Duration',
      name: 'duration',
      type: INPUT_TYPE.TEXT_ONLY,
      value: `${duration}h`,
      inputSettings: { show: true },
      sideFields: [
        {
          label: 'Date',
          name: 'date',
          type: INPUT_TYPE.DATE_PICKER,
          defaultValue: dateData || undefined,
          inputSettings: {
            disabledPastDate: true,
          },
          size: 9,
        },
        {
          label: 'Time',
          name: 'signedTime',
          type: INPUT_TYPE.TIME_PICKER,
          defaultValue: signedTimeData || undefined,
          size: 9,
        },
      ],
    },
    // {
    //   label: 'Time',
    //   name: 'signedTime',
    //   type: INPUT_TYPE.TIME_PICKER,
    //   defaultValue: signedTimeData || undefined,
    //   sideFields: [
    //     {
    //       label: 'Date',
    //       name: 'date',
    //       type: INPUT_TYPE.DATE_PICKER,
    //       defaultValue: dateData || undefined,
    //       inputSettings: {
    //         disabledPastDate: true,
    //       },
    //       size: 12,
    //     },
    //   ],
    // },
    {
      label: 'Address Line 1',
      name: 'address1',
      addressSuggestion: true,
      addressSuggestionSubFields: {
        address1: 'address1',
        city: 'city',
        zip: 'zip',
        state: 'state',
        longtitude: 'longtitude',
        latitude: 'latitude',
      },
    },
    { label: 'Address Line 2', name: 'address2' },
    { label: 'City', name: 'city' },
    {
      label: 'Zip',
      name: 'zip',
      sideFields: [
        { label: 'State', name: 'state', type: INPUT_TYPE.SELECT, size: 12, inputOptions: usStatesSelectOptions },
      ],
    },
  ];

  const shippingLabelFormFields: InputProps[] = useMemo(() => {
    const toggleField: InputProps[] = [
      {
        name: 'autoGenShippingLabel',
        label: 'Autogenerate Return Shipping Label',
        type: INPUT_TYPE.SWITCH,
        value: !labelProvidedByClient,
        onChange: (v: boolean) => {
          setLabelProvidedByClient(!v);
        },
      },
    ];
    const uploadField: InputProps[] = labelProvidedByClient
      ? [
          {
            name: 'labelFiles',
            label: 'Return Document Label',
            type: INPUT_TYPE.FILE,
            inputSettings: { uploadStyle: 'small', singleFileOnly: true },
          },
        ]
      : [
          {
            name: 'shippingServiceType',
            label: 'Shipping Service Type',
            type: INPUT_TYPE.SELECT,
            inputOptions: Object.entries(ShippingServiceType).map(([k, v]) => ({ value: k, label: v })),
          },
        ];
    return [...toggleField, ...uploadField];
  }, [labelProvidedByClient]);

  const onSubmit = async (value: any) => {
    // temporarily remove type = RON so hardcoded type = IN PERSON here
    const finalValue = { ...value, type: AppointmentNotaryType.IN_PERSON };
    const { fileGroup, ...submitValue } = finalValue;
    submitValue.fileIds = Object.entries(fileGroup)
      .filter(([_, isSelected]) => isSelected)
      .map(([fileId]) => fileId);
    // closeModal();
    if (modalType === 'addNotaryAppointment') {
      addAppointment(submitValue, `new_${uuidV4()}`);
    } else {
      editAppointment(submitValue, data?.id || data?.key || '');
    }
  };

  const onCancel = () => {
    closeModal();
  };

  const isAddModal = modalType === 'addNotaryAppointment';
  const modalMainText = isAddModal ? 'Add Appointment' : 'Edit Appointment';
  const schema = { ...shippingLabelSchema, ...appointmentSchema };

  return (
    <DefaultModal
      props={{
        title: modalMainText,
        visible: modalType === 'addNotaryAppointment' || modalType === 'editNotaryAppointment',
        destroyOnClose: true,
        onCancel,
        maskClosable: false,
        bodyStyle: { maxHeight: '80vh', overflowY: 'auto' },
      }}>
      {signatoriesSelectOptions.length === 0 ? (
        <div>
          <div>
            One or more signatory must be created for this transaction before an appointment can be created. Please
            close this window and create appointment signatories before trying to create an appointment.
          </div>
          <div className='mt-3'>
            <CancelButton onClick={closeModal}>Close</CancelButton>
          </div>
        </div>
      ) : (
        <Formik initialValues={notaryAppointmentInitValues} onSubmit={onSubmit} validationSchema={Yup.object(schema)}>
          <Form className='gx-form-row0'>
            <InputFieldsGenerator formFields={formFields} />
            <InputComponent
              style={{ padding: '0 16px', marginTop: 12 }}
              name='fileGroup'
              label='Documents for Appointment'
              labelStyle={{ fontWeight: 500, marginBottom: 15 }}
              labelType={LABEL_TYPE.above}
              type={INPUT_TYPE.CHECKBOX_GROUP}
              checkboxGroupProps={checkboxProps}
              inputSettings={{ noDataRender: 'No available documents' }}
            />
            <div className='mt-3'>
              <InputFieldsGenerator formFields={shippingLabelFormFields} />
              {fileUploadLoading && labelProvidedByClient && (
                <div style={{ padding: '10px 16px' }}>
                  <Progress percent={filePercentageUpload} strokeColor='#038fde' />
                </div>
              )}
            </div>
            <Row>
              <Col flex='auto' className='ml-4 mt-2'>
                <ConfirmButton
                  width='144px'
                  isLoading={isLoading}
                  buttonType='primary'
                  htmlType='submit'
                  className='gx-mb-0'>
                  {modalMainText}
                </ConfirmButton>
                <CancelButton className='gx-mb-0' htmlType='button' onClick={onCancel}>
                  <IntlMessages id='appModule.cancel' />
                </CancelButton>
              </Col>
            </Row>
          </Form>
        </Formik>
      )}
    </DefaultModal>
  );
};

export default React.memo(AppointmentModal);
